import React, { useEffect, useState, useMemo } from 'react';
import { fetchProducts, deleteProduct } from '../api/products';
import { toast } from 'react-toastify';
import ProductModal from '../components/ProductModal';
import './products.css';

const Products = () => {
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const loadProducts = async () => {
    setLoading(true);
    try {
      const res = await fetchProducts();
      setProducts(res.data.products || []);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const openAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };
  const openEdit = (p) => {
    setEditData(p);
    setModalOpen(true);
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"?`)) return;
    try {
      await deleteProduct(product.id);
      toast.success('Product deleted');
      loadProducts();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to delete product');
    }
  };

  // Get unique categories from products
  const categories = useMemo(() => {
    const cats = new Set(products.map(p => p.category).filter(Boolean));
    return ['all', ...Array.from(cats).sort()];
  }, [products]);

  // Filter products by selected category
  const filteredProducts = useMemo(() => {
    let list = products;
    if (selectedCategory !== 'all') {
      list = list.filter(p => p.category === selectedCategory);
    }
    if (search.trim()) {
      const term = search.toLowerCase();
      list = list.filter(p =>
        p.name.toLowerCase().includes(term) ||
        (p.description || '').toLowerCase().includes(term)
      );
    }
    return list;
  }, [products, selectedCategory, search]);

  return (
    <div className="products-page">
      <div className="page-header">
        <h2>Products</h2>
        <div className="filters">
          <input
            type="text"
            placeholder="Search products…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <select 
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-filter"
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category === 'all' ? 'All Categories' : category}
              </option>
            ))}
          </select>
          <button onClick={openAdd}>Add Product</button>
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table className="products-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Qty</th>
              <th>Import</th>
              <th>Selling</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center' }}>
                  No products found{selectedCategory !== 'all' ? ` in category: ${selectedCategory}` : ''}
                </td>
              </tr>
            ) : (
              filteredProducts.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{p.category}</td>
                  <td>{p.quantity}</td>
                  <td>{p.import_price}</td>
                  <td>{p.selling_price}</td>
                  <td>{p.status}</td>
                  <td className="actions-cell">
                    <button onClick={() => openEdit(p)}>Edit</button>
                    <button onClick={() => handleDelete(p)} style={{ marginLeft: 8 }}>Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {modalOpen && (
        <ProductModal
          isOpen={modalOpen}
          onClose={(refresh) => {
            setModalOpen(false);
            if (refresh) loadProducts();
          }}
          initialData={editData}
        />
      )}
    </div>
  );
};

export default Products;
